import { Organization } from '@quadminds/client-apis/auth';
import { useGetUserInfo, useNavigateWithCurrentQueryParams, useSetCurrentOrganization } from '@quadminds/hooks';
import { isDevelopment } from '@quadminds/shared/utils';
import axios from 'axios';
import { FirebaseError } from 'firebase/app';
import { useCallback, useEffect, useState } from 'react';
import SelectOrganizationComponent from '../components/SelectOrganizationComponent/SelectOrganizationComponent';
import { useLogin } from '../hooks/useLogin';
import DefaultLayout from '../layout/DefaultLayout';
import { AuthApiError } from '../services/auth/auth-api-error';
import { handleAuthApiError, handleFirebaseError } from '../utils';

function SelectOrganization() {
    const [selectedOrganization, setSelectedOrganization] = useState<string | null>(null);
    const { setHasToRedirect } = useLogin();
    const { data: user, isLoading, isError } = useGetUserInfo({ refetchInterval: isDevelopment() ? false : 10000 });
    const { mutateAsync: selectOrg, isLoading: isSelectOrgLoading } = useSetCurrentOrganization();
    const navigate = useNavigateWithCurrentQueryParams();

    const selectOrgHandler = useCallback(
        async (id: string) => {
            setSelectedOrganization(id);
            try {
                const { accessToken, refreshToken } = await selectOrg(id);
                // Store the new token and redirect
                localStorage.setItem('token', accessToken);
                localStorage.setItem('refreshToken', refreshToken);
                setHasToRedirect(true);
            } catch (rawError) {
                const error =
                    axios.isAxiosError(rawError) && rawError.response?.data?.code
                        ? new AuthApiError(rawError.response.data.code, rawError.response.data.message)
                        : rawError;
                const errorPath =
                    error instanceof FirebaseError
                        ? handleFirebaseError(error)
                        : error instanceof AuthApiError
                        ? handleAuthApiError(error)
                        : undefined;
                if (errorPath) {
                    const [path, state] = errorPath;
                    navigate({ newPath: path, navigateOptions: { ...state } });
                } else {
                    navigate({ newPath: '/error' });
                }
            }
        },
        [selectOrg, setHasToRedirect, navigate],
    );

    // Safe redirect to login if user has less than 1 organization
    useEffect(() => {
        // Redirect to login if an error occurs
        if (isError && !isLoading) {
            navigate({ newPath: '/login', ignoredParams: [] });
        }
        // Redirect to login if user has less than 1 organization
        if (user?.organizations && user?.organizations?.length < 1 && !isLoading) {
            setHasToRedirect(true);
        }
    }, [user, navigate, isError, isLoading, setHasToRedirect]);

    return (
        <DefaultLayout isLoading={isLoading}>
            {user && (
                <SelectOrganizationComponent
                    organizations={user?.organizations as Organization[]}
                    selectHandler={selectOrgHandler}
                    isSelectOrgLoading={isSelectOrgLoading}
                    selectedOrganization={selectedOrganization}
                />
            )}
        </DefaultLayout>
    );
}

export default SelectOrganization;
